'use client'
import { useState, useEffect } from 'react'
import { ONE } from 'config/apis'
import { torsCats } from 'config/const'
import { dateToString } from 'utils/date'
import { capitalize } from 'utils/strings'
import Header from 'components/Header'
import PopoverButton from 'components/PopoverButton'
import Table from 'components/Table'
import {
  Document,
  Page,
  StyleSheet,
  View,
  Text,
  PDFDownloadLink
} from '@react-pdf/renderer'

const defaultYear = new Date().getFullYear()
const tableHeaders = ['DATA', 'TORNEIG', 'CATEGORIA', 'DELEGACIONS']

const TableCalendar = ({
  defaultCategory = { id: '', name: '' },
  isClub = false
}) => {
  const popoverOptions = [
    {
      text: 'Calendari en llistat',
      link: '/competicio/calendari/llistat'
    },
    {
      text: 'Calendari per fitxes',
      link: '/competicio/calendari/fitxes'
    }
  ]
  const isRankingCategorized = defaultCategory?.keyword
  let filtersBase = [
    {
      name: 'year',
      keyName: 'Years',
      options: [{ Id: defaultYear, Value: defaultYear }]
    },
    !isRankingCategorized && {
      name: 'category',
      keyName: 'Categories',
      options: [{ Id: '', Value: 'Calendari general' }]
    },
    {
      name: 'playing',
      options: [
        { Id: '1', Value: 'En joc' },
        { Id: '', Value: 'Tots' }
      ]
    },
    {
      name: 'state',
      options: [
        { Id: '', Value: 'Inscripcions' },
        { Id: 'yes', Value: 'Inscripcions obertes' },
        { Id: 'no', Value: 'Inscripcions tancades' }
      ]
    },
    {
      name: 'delegation',
      keyName: 'Delegations',
      options: [{ Id: '', Value: 'Província' }]
    }
  ].filter((i) => i)
  const [resultsByPage, setResultsByPage] = useState(8)
  const [torsTable, setTorsTable] = useState()
  const [filters, setFilters] = useState(filtersBase)
  const [activeFilters, setActiveFilters] = useState({
    year: defaultYear,
    category: defaultCategory.id,
    delegation: '',
    registration: '',
    playing: '1',
    state: '',
    page: 1
  })
  const [pageDynamically, setPageDynamically] = useState(1)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const [pdfData, setPDFData] = useState([])
  const styles = StyleSheet.create({
    table: {
      width: '100%',
      fontSize: '8px',
      paddingTop: '30px',
      paddingLeft: '30px'
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      borderTop: '1px solid #EEE',
      paddingTop: 8,
      paddingBottom: 8
    },
    header: {
      borderTop: 'none'
    },
    row1: {
      width: '20%'
    },
    row2: {
      width: '40%'
    },
    row3: {
      width: '20%'
    },
    row4: {
      width: '20%'
    }
  })

  const handleScroll = () => {
    const position = window.pageYOffset
    setScrollPosition(position)
  }

  const getFilters = async () => {
    try {
      const reqFilters = await fetch(ONE.TOR_FILTERS, ONE.HEADERS)
      const filtersData = (await reqFilters.json()).Data
      filtersBase = filters.map((item) => {
        if (filtersData?.[item.keyName]) {
          // Avoid repeated year
          if (
            filtersData[item.keyName].find(
              ({ Value }) => Value === item.options[0].Value
            )
          ) {
            item.options = filtersData[item.keyName]
          } else {
            item.options = [item.options[0], ...filtersData[item.keyName]]
          }
        }
        return item
      })
      setFilters(filtersBase)
      if (filtersData?.Years?.[0].Value > defaultYear) {
        const newFilter = { ...activeFilters }
        newFilter.year = filtersData.Years[0].Value
        setActiveFilters(newFilter)
      }
    } catch (err) {
      console.log(err)
    }
  }

  const getUrlFromTor = (tor) => {
    // We must check first CategoryFilter for tors as universitaries which
    // is kind of a subcategory
    const torDetails = torsCats.find(
      ({ id }) => tor.CategoryFilter.toLowerCase() === id
    )
    let torUrl = '/competicio/calendari/llistat'
    if (torDetails && tor?.Slug) {
      torUrl = `${torDetails.torUrl}/${tor.Year}/${tor.Slug}/informacio`
    }
    return torUrl
  }

  const torsForTable = (items) =>
    items.Data.map((tor) => ({
      date: `${dateToString(tor.StartDate).withYearMinimal} - ${
        dateToString(tor.EndDate).withYearMinimal
      }`,
      name: tor.Name,
      category: capitalize(tor.Category),
      delegation: capitalize(tor.Place),
      link: getUrlFromTor(tor)
    }))

  const getTors = async (params) => {
    try {
      const res = await fetch(
        `${ONE.TOR_LIST}?year=${activeFilters.year}&playing=${
          activeFilters.playing
        }&category=${activeFilters.category}&delegation=${
          activeFilters.delegation
        }&registrationOpen=${activeFilters.state}&page=${
          activeFilters.page
        }&limit=${params?.forceResults || resultsByPage}`,
        ONE.HEADERS
      )
      const _tors = await res.json()
      setTotalRecords(_tors.TotalRecords)
      setTorsTable(torsForTable(_tors))
      const resPDF = await fetch(
        `${ONE.TOR_LIST}?year=${activeFilters.year}&playing=${activeFilters.playing}&category=${activeFilters.category}&delegation=${activeFilters.delegation}&registrationOpen=${activeFilters.state}&page=${activeFilters.page}`,
        ONE.HEADERS
      )
      const _pdf = await resPDF.json()
      setPDFData(torsForTable(_pdf) || [])
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    const newFilter = { ...activeFilters }
    newFilter.page = pageDynamically
    setActiveFilters(newFilter)
  }, [pageDynamically])

  useEffect(() => {
    getTors()
  }, [activeFilters])

  useEffect(() => {
    getFilters()
    getTors()
    window.addEventListener('scroll', handleScroll, { passive: true })
  }, [])

  return (
    <>
      {!isClub && (
        <Header
          img="https://fcp-site-images.s3.eu-west-3.amazonaws.com/3_Calendari_9135bd3e46.jpg"
          title="Calendari"
          content={
            <PopoverButton
              text="Canviar vista"
              icon="eye"
              options={popoverOptions}
              scrollPositionMax={550}
              scrollPositionCurrent={scrollPosition}
            />
          }
        />
      )}
      <div className={`${!isClub && 'my-10 lg:my-28 mx-8 lg:mx-22 xl:mx-44'}`}>
        <div className="grid grid-cols-1 gap-2 lg:flex bg-sec-grey-4 rounded-lg justify-center py-4 lg:py-6 mb-8 px-4">
          {filters &&
            filters.map((item, idx) => {
              return (
                <div key={idx} className="w-full lg:w-auto">
                  <select
                    name={item?.name}
                    className="bg-grey w-full border-none text-blue font-alliance-medium text-[18px] lg:text-[20px] h-12 lg:h-16 rounded-sm focus:ring-0 mr-4 min-w-[140px]"
                    onChange={(e) => {
                      const newFilter = { ...activeFilters }
                      newFilter[e.target.name] = e.target.value
                      setActiveFilters(newFilter)
                    }}
                  >
                    {item?.options?.map((option, oidx) => {
                      return (
                        <option
                          key={oidx}
                          value={option?.Id}
                          defaultValue={
                            (item.name === 'year' &&
                              option.Value === defaultYear) ||
                            (item.name === 'category' &&
                              option.Value === defaultCategory.name)
                          }
                        >
                          &nbsp;&nbsp;&nbsp;{option?.Value}
                        </option>
                      )
                    })}
                  </select>
                </div>
              )
            })}
          {pdfData && pdfData.length ? (
            <PDFDownloadLink
              key={Date.now()}
              document={
                <Document>
                  <Page>
                    <View>
                      <View style={styles.table}>
                        <View style={[styles.row, styles.header]}>
                          <Text style={styles.row1}>Data</Text>
                          <Text style={styles.row2}>Torneig</Text>
                          <Text style={styles.row3}>Categoria</Text>
                          <Text style={styles.row4}>Delegacions</Text>
                        </View>
                        {pdfData.map((row, idx) => (
                          <View key={idx} style={styles.row} wrap={false}>
                            <Text style={styles.row1}>{row.date}</Text>
                            <Text style={styles.row2}>{row.name}</Text>
                            <Text style={styles.row3}>{row.category}</Text>
                            <Text style={styles.row4}>{row.delegation}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  </Page>
                </Document>
              }
              fileName={`Calendari_${activeFilters?.year}${
                activeFilters?.category ? `_${activeFilters?.category}` : ''
              }${activeFilters?.playing && '_En-joc'}${
                activeFilters?.delegation ? `_${activeFilters?.delegation}` : ''
              }.pdf`}
              style={{
                width: '35px',
                marginLeft: '25px',
                marginTop: '15px'
              }}
            >
              <img src="/assets/icons/download.svg" />
            </PDFDownloadLink>
          ) : null}
        </div>
        {torsTable && (
          <Table
            headers={tableHeaders}
            content={torsTable}
            cols={4}
            rows={resultsByPage}
            pageDynamically={pageDynamically}
            setPageDynamically={setPageDynamically}
            totalRecords={totalRecords}
          />
        )}
      </div>
    </>
  )
}

export default TableCalendar
